import { useEffect, useState, useCallback } from "react";
import FarrierAppointments from "../components/FarrierAppointments";
import { confirmAction } from "../components/ConfirmDialog";
import { useSearchParams } from "react-router-dom";
import { useStable } from "../context/StableContext";
import { InfoHint } from "../components/InfoHint";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateField } from "../components/DateField";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, ChevronDown, ChevronRight, Search, History, CalendarDays } from "lucide-react";
import { HorsePicker } from "../components/HorsePicker";
import { FutureDateWarning } from "../components/FutureDateWarning";
import { toast } from "sonner";
import StatusDot from "../components/StatusDot";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ViewTabs } from "../components/ViewTabs";
import { ContextBanner } from "../components/ContextBanner";
import { timeToDue } from "../lib/timeToDue";
import { matchHorse } from "../lib/horseSearch";

const PERIODS = [["7", "7 giorni"], ["15", "15 giorni"], ["21", "21 giorni"], ["30", "30 giorni"], ["60", "60 giorni"], ["all", "Tutto"]];
const daysUntil = (iso) => { const d = new Date(iso + "T00:00:00"); const t = new Date(); t.setHours(0, 0, 0, 0); return (d - t) / 86400000; };

const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—");
const fmtShort = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long" }) : "—");
const fmtDT = (x) => {
  if (!x) return "";
  const d = new Date(x);
  const ds = d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  return String(x).includes("T") ? `${ds} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : ds;
};
const addMonthsISO = (iso, m) => {
  const d = new Date(iso + "T00:00:00");
  const day = d.getDate();
  d.setMonth(d.getMonth() + m);
  if (d.getDate() < day) d.setDate(0);
  return d.toISOString().slice(0, 10);
};
const farrierStatus = (nextDate) => {
  if (!nextDate) return "futuro";
  const d = new Date(nextDate + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = (d - today) / 86400000;
  if (diff < 0) return "scaduto";
  if (diff <= 30) return "in_scadenza";
  return "futuro";
};
const EMPTY = { horse_id: "", horse_ids: [], overrides: {}, date: new Date().toISOString().slice(0, 10), work_type: "", farrier: "", cost: "", notes: "", next_date: "" };

export default function Farrier() {
  const { current, perms } = useStable();
  const { user, updatePreferences } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [horses, setHorses] = useState([]);
  const [types, setTypes] = useState([]);
  const [horse, setHorse] = useState(params.get("horse") || "all");
  const [dialog, setDialog] = useState(null);
  const [postponeItem, setPostponeItem] = useState(null);
  const [postponeDate, setPostponeDate] = useState("");
  const [period, setPeriod] = useState(user?.preferences?.farrier_period || "15");
  const [tab, setTab] = useState("upcoming");
  const [hq, setHq] = useState("");
  const [prog, setProg] = useState(null);
  const [progDate, setProgDate] = useState("");
  const [progBusy, setProgBusy] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [histAction, setHistAction] = useState(null);
  const [recEdit, setRecEdit] = useState(null);
  const [recEditConfirm, setRecEditConfirm] = useState(false);
  const [recSaving, setRecSaving] = useState(false);

  const changePeriod = (v) => {
    setPeriod(v); updatePreferences({ farrier_period: v });
    const p = new URLSearchParams(params); p.delete("all"); p.delete("focus"); p.delete("group"); setParams(p, { replace: true });
  };

  const load = useCallback(() => {
    if (!current) return;
    setItems(null);
    const q = horse === "all" ? {} : { horse_id: horse };
    api.get(`/stables/${current.id}/farrier`, { params: q }).then((r) => setItems(r.data));
    api.get(`/stables/${current.id}/farrier/upcoming`).then((r) => setUpcoming(r.data));
  }, [current, horse]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("all") === "1" || params.get("focus") || params.get("date") || params.get("group")) setTab("upcoming");
  }, [params]);
  useEffect(() => { const p = user?.preferences?.farrier_period; if (p) setPeriod(p); }, [user?.preferences?.farrier_period]);
  useEffect(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/horses`).then((r) => setHorses(r.data));
    api.get(`/stables/${current.id}/farrier-types`).then((r) => setTypes(r.data));
  }, [current]);

  useEffect(() => {
    if (current && params.get("new") === "1" && perms.canOps) {
      const h = params.get("horse");
      setDialog({ ...EMPTY, horse_id: h && h !== "all" ? h : "" });
      const p = new URLSearchParams(params); p.delete("new"); setParams(p, { replace: true });
    }
  }, [current, params]); // eslint-disable-line react-hooks/exhaustive-deps


  const remove = async (r) => {
    const ok = await confirmAction({ title: "Eliminare definitivamente questo intervento?",
      description: "L'eliminazione rimuove il record e NON ripristina automaticamente l'intervento precedente.",
      confirmLabel: "Elimina definitivamente", cancelLabel: "Annulla", danger: true });
    if (!ok) return;
    await api.delete(`/stables/${current.id}/farrier/${r.id}`);
    toast.success("Intervento eliminato"); load();
  };
  const cancelIntervento = async (r) => {
    const ok = await confirmAction({ title: "Annullare questo intervento?",
      description: "L'intervento resterà nello storico come annullato. Se disponibile, il precedente riferimento Mascalcia verrà ripristinato.",
      confirmLabel: "Annulla intervento", cancelLabel: "Indietro", danger: false });
    if (!ok) return;
    try { await api.post(`/stables/${current.id}/farrier/${r.id}/cancel`); toast.success("Intervento annullato"); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const postpone = async (recordId, payload) => {
    try {
      await api.post(`/stables/${current.id}/farrier/${recordId}/postpone`, payload);
      toast.success("Prossima data aggiornata");
      setPostponeItem(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const correctPostpone = async () => {
    if (!prog || !progDate) return;
    setProgBusy(true);
    try {
      await api.post(`/stables/${current.id}/farrier/${prog.id}/postpone/correct`, { new_date: progDate });
      toast.success("Ultima data prevista corretta");
      setProg(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setProgBusy(false); }
  };

  const undoPostpone = async () => {
    if (!prog) return;
    setProgBusy(true);
    try {
      await api.post(`/stables/${current.id}/farrier/${prog.id}/postpone/undo`);
      toast.success("Ultimo posticipo annullato");
      setProg(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setProgBusy(false); }
  };

  const doClearProgramming = async () => {
    if (!prog) return;
    setProgBusy(true);
    try {
      await api.post(`/stables/${current.id}/farrier/${prog.id}/clear-programming`);
      toast.success("Programmazione eliminata");
      setClearConfirm(false); setProg(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setProgBusy(false); }
  };

  // Apre il form di correzione di un record STORICO (solo dati documentali; non tocca la programmazione futura).
  const openRecordEdit = (f) => {
    if (!f) return;
    setRecEdit({ id: f.id, horse_name: f.horse_name, date: f.date, work_type: f.work_type || "",
      farrier: f.farrier || "", cost: f.cost ?? "", notes: f.notes || "" });
    setHistAction(null);
  };

  const saveRecordEdit = async () => {
    if (!recEdit) return;
    setRecSaving(true);
    const payload = { date: recEdit.date, work_type: recEdit.work_type, farrier: recEdit.farrier || null,
      cost: recEdit.cost === "" ? null : Number(recEdit.cost), notes: recEdit.notes || null };
    try {
      await api.put(`/stables/${current.id}/farrier/${recEdit.id}/record`, payload);
      toast.success("Record storico aggiornato");
      setRecEditConfirm(false); setRecEdit(null); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setRecSaving(false); }
  };

  // Apre il dialog "Programmazione" con la cronologia dei posticipi.
  // readonly=true nello Storico (sola consultazione), false nei Prossimi (azioni operative).
  const openProg = (rec, readonly) => {
    setProg({
      id: rec.farrier_id || rec.id, horse_name: rec.horse_name, next_date: rec.next_date,
      next_date_changes: rec.next_date_changes || [], readonly,
    });
    setProgDate((rec.next_date || "").slice(0, 10));
  };

  // Tap su una riga dei "Prossimi interventi" -> apre il form "Registra intervento"
  // precompilato col cavallo, data odierna e nuova prossima data proposta dal ciclo attuale.
  const openRegister = (u) => {
    const today = new Date().toISOString().slice(0, 10);
    let proposedNext = "";
    if (u.date && u.next_date) {
      const cycleDays = Math.round((new Date(u.next_date + "T00:00:00") - new Date(u.date + "T00:00:00")) / 86400000);
      if (cycleDays > 0) {
        const d = new Date(today + "T00:00:00");
        d.setDate(d.getDate() + cycleDays);
        proposedNext = d.toISOString().slice(0, 10);
      }
    }
    setDialog({ ...EMPTY, horse_ids: [u.horse_id], work_type: u.work_type || "", farrier: u.farrier || "", date: today, next_date: proposedNext });
  };

  const seeAll = params.get("all") === "1";
  const focusHorse = params.get("focus");
  const focusDate = params.get("date");
  const groupParam = params.get("group"); // overdue | upcoming (Dashboard deep-link subset)
  const effectivePeriod = (seeAll || focusDate || groupParam) ? "all" : period;
  let displayUpcoming = [...upcoming].filter((u) => {
    if (focusDate) return String(u.next_date).slice(0, 10) === focusDate;
    if (farrierStatus(u.next_date) === "scaduto") return true;
    if (effectivePeriod === "all") return true;
    return daysUntil(u.next_date) <= Number(effectivePeriod);
  });
  const focusRow = focusHorse ? upcoming.find((u) => u.horse_id === focusHorse) : null;
  if (focusRow && !displayUpcoming.some((u) => u.horse_id === focusHorse)) displayUpcoming = [focusRow, ...displayUpcoming];
  displayUpcoming.sort((a, b) => String(a.next_date).localeCompare(String(b.next_date)));
  const clearContext = () => { const p = new URLSearchParams(params); p.delete("all"); p.delete("focus"); p.delete("date"); p.delete("group"); setParams(p, { replace: true }); };

  const hqLower = hq.trim().toLowerCase();
  const searchedUpcoming = hqLower ? displayUpcoming.filter((u) => matchHorse(u, hq)) : displayUpcoming;
  const allOverdue = searchedUpcoming.filter((u) => farrierStatus(u.next_date) === "scaduto");
  const allProssimi = searchedUpcoming.filter((u) => farrierStatus(u.next_date) !== "scaduto");
  const showOverdueG = !groupParam || groupParam === "overdue";
  const showUpcomingG = !groupParam || groupParam === "upcoming";
  const upScaduti = showOverdueG ? allOverdue : [];
  const upProssimi = showUpcomingG ? allProssimi : [];
  const GROUP_LABEL = { overdue: "Scaduti", upcoming: "In programma" };
  const groupCount = groupParam === "overdue" ? allOverdue.length : groupParam === "upcoming" ? allProssimi.length : 0;
  const contextLabel = groupParam ? `${GROUP_LABEL[groupParam]} · ${groupCount}`
    : focusDate ? `Interventi del ${fmt(focusDate)}` : (seeAll ? "tutti gli interventi" : (focusRow ? `${focusRow.horse_name} · ${fmt(focusRow.next_date)}` : ""));
  const searchedHistory = items ? (hqLower ? items.filter((f) => matchHorse(f, hq)) : items) : null;

  const renderUpcoming = (u) => {
    const st = farrierStatus(u.next_date);
    const tt = timeToDue(u.next_date);
    const changes = u.next_date_changes || [];
    const totDays = changes.reduce((s, c) => s + (c.days || 0), 0);
    const rowInner = (
      <>
        <p className="font-semibold text-[#1B3B2B] truncate group-hover:text-[#8C532B] transition-colors">{u.horse_name}</p>
        <p className="text-xs text-[#41504A] truncate">{u.work_type}</p>
        <p className="text-xs font-medium mt-0.5 flex flex-wrap gap-x-1.5" data-testid={`upcoming-last-${u.horse_id}`}>
          <span className="text-[#5F6B64]">Ultimo: {u.date ? fmtShort(u.date) : "—"}</span>
          {st === "scaduto"
            ? <span style={{ color: tt.color }}>· {tt.label}</span>
            : <span style={{ color: tt.color }}>· Previsto: {fmt(u.next_date)}</span>}
        </p>
      </>
    );
    return (
      <div key={u.farrier_id} data-testid={`upcoming-farrier-${u.horse_id}`} className={`px-3 py-2.5 ${focusHorse === u.horse_id ? "ring-2 ring-[#8C532B] ring-inset bg-[#FFFBEB]" : ""}`}>
        <div className="flex items-center gap-3">
          <StatusDot status={tt.dot} />
          {perms.canOps ? (
            <button type="button" data-testid={`register-from-upcoming-${u.horse_id}`} onClick={() => openRegister(u)}
              className="min-w-0 flex-1 text-left group cursor-pointer -my-2 py-2 rounded-lg hover:bg-[#FAF8F5] transition-colors">
              {rowInner}
            </button>
          ) : (
            <div className="min-w-0 flex-1">{rowInner}</div>
          )}
          {perms.canOps && (
            <div className="flex items-center gap-1.5 shrink-0">
            <button data-testid={`prog-access-${u.farrier_id}`} onClick={(e) => { e.stopPropagation(); openProg(u, false); }} title="Programmazione"
              className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-[#E5DEC9] bg-white text-[#8C532B] hover:border-[#8C532B] transition-colors">
              <CalendarDays className="h-3.5 w-3.5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`postpone-menu-${u.farrier_id}`} onClick={(e) => e.stopPropagation()}
                  className="shrink-0 inline-flex items-center gap-1 text-xs rounded-full border border-[#E5DEC9] bg-white px-2.5 py-1 text-[#8C532B] hover:border-[#8C532B] transition-colors">
                  Posticipa <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {[7, 10, 14].map((dd) => (
                  <DropdownMenuItem key={dd} data-testid={`postpone-${u.farrier_id}-${dd}`} onClick={() => postpone(u.farrier_id, { days: dd })}>
                    +{dd} giorni
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem data-testid={`postpone-pick-${u.farrier_id}`}
                  onClick={() => { setPostponeItem({ id: u.farrier_id, horse_name: u.horse_name, next_date: u.next_date }); setPostponeDate((u.next_date || "").slice(0, 10)); }}>
                  Data personalizzata…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          )}
        </div>
        {changes.length > 0 && (
          <button type="button" data-testid={`prog-open-${u.farrier_id}`} onClick={() => openProg(u, false)}
            className="mt-1.5 ml-[26px] inline-flex items-center gap-1 text-xs text-[#8C532B] hover:text-[#5A3419] transition-colors">
            <History className="h-3 w-3" />
            {totDays > 0 ? `Posticipato +${totDays} gg` : `${changes.length} variazioni programmazione`}
            <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 eq-fade-up">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="font-mono-label text-[#8C532B] text-xs">Interventi e programmazione</p>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-[#1B3B2B]">Mascalcia</h1>
        </div>
        {perms.canOps && (
          <Button data-testid="add-farrier-btn" size="sm" onClick={() => setDialog({ ...EMPTY })} className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Registra intervento</span>
          </Button>
        )}
      </div>

      <ViewTabs value={tab} onChange={setTab}
        options={[{ value: "upcoming", label: "Prossimi interventi", testid: "tab-upcoming" }, { value: "history", label: "Storico", testid: "tab-history" }]} />

      {tab === "upcoming" && (
        <div className="mt-4">
          <section data-testid="farrier-upcoming-section">
            {contextLabel && (
              <div className="mb-3"><ContextBanner label={contextLabel} onClear={clearContext} testid="farrier-context-banner" /></div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
                <input data-testid="farrier-search" value={hq} onChange={(e) => setHq(e.target.value)} placeholder="Cerca cavallo…"
                  className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-[#E5DEC9] text-sm outline-none placeholder:text-[#9aa39c]" />
              </div>
              <div className="hidden lg:flex flex-wrap gap-1.5">
                {PERIODS.map(([v, l]) => (
                  <button key={v} data-testid={`farrier-period-${v}`} onClick={() => changePeriod(v)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${period === v ? "bg-[#1B3B2B] text-white border-[#1B3B2B]" : "bg-white text-[#5F6B64] border-[#E5DEC9] hover:border-[#8C532B]"}`}>{l}</button>
                ))}
              </div>
              <div className="lg:hidden flex items-center gap-2">
                <span className="text-sm text-[#5F6B64] shrink-0">Periodo:</span>
                <Select value={period} onValueChange={changePeriod}>
                  <SelectTrigger className="flex-1 bg-white h-9" data-testid="farrier-period-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <FarrierAppointments stableId={current.id} canOps={perms?.canOps} compact reference />
            {upScaduti.length === 0 && upProssimi.length === 0 ? (
              <div className="bg-white rounded-xl border border-[#E5DEC9] px-4 py-6 text-center text-sm text-[#5F6B64] mt-4">Nessun intervento scaduto o in scadenza nel periodo selezionato.</div>
            ) : (
              <div className="space-y-4 mt-4">
                {upScaduti.length > 0 && (
                  <div data-testid="farrier-overdue-group">
                    <h3 className="font-mono-label text-xs uppercase text-[#DC2626] mb-1.5 px-1">Scaduti</h3>
                    <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">{upScaduti.map(renderUpcoming)}</div>
                  </div>
                )}
                {upProssimi.length > 0 && (
                  <div data-testid="farrier-upcoming-group">
                    <h3 className="font-mono-label text-xs uppercase text-[#8C532B] mb-1.5 px-1 flex items-center gap-1.5">Prossimi interventi <InfoHint testId="help-farrier-plan" label="Prossimo intervento e storico" text="La data del prossimo intervento è una pianificazione. Lo storico registra invece gli interventi realmente eseguiti." /></h3>
                    <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">{upProssimi.map(renderUpcoming)}</div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {tab === "history" && (
        <div className="mt-4">
          <section data-testid="farrier-history-section">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
                <input data-testid="farrier-history-search" value={hq} onChange={(e) => setHq(e.target.value)} placeholder="Cerca cavallo…"
                  className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-[#E5DEC9] text-sm outline-none placeholder:text-[#9aa39c]" />
              </div>
              <Select value={horse} onValueChange={setHorse}>
                <SelectTrigger className="w-full sm:w-[170px] bg-white h-9" data-testid="farrier-horse-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i cavalli</SelectItem>
                  {horses.map((h) => <SelectItem key={h.id} value={h.id}>{h.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!searchedHistory ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>
            ) : searchedHistory.length === 0 ? (
              <div className="bg-white rounded-xl border border-[#E5DEC9] py-12 text-center text-[#5F6B64]">Nessun intervento registrato.</div>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">
                {searchedHistory.map((f) => {
                  const changes = f.next_date_changes || [];
                  const totDays = changes.reduce((s, c) => s + (c.days || 0), 0);
                  return (
                    <div key={f.id} data-testid={`farrier-row-${f.id}`} className={`px-3 py-2.5 ${f.status === "cancelled" ? "opacity-60 bg-[#FBFAF8]" : ""}`}>
                      <div className="flex items-start gap-2">
                        <button type="button" data-testid={`open-farrier-record-${f.id}`} onClick={() => setHistAction(f)}
                          className="flex-1 min-w-0 text-left group -my-1 py-1 rounded-lg hover:bg-[#FAF8F5] transition-colors">
                          <p className="font-semibold text-[#1B3B2B] truncate group-hover:text-[#8C532B] transition-colors flex items-center gap-1.5">
                            <span className="truncate">{f.horse_name}</span>
                            {f.status === "cancelled" && <span data-testid={`farrier-cancelled-badge-${f.id}`} className="shrink-0 text-[9px] font-mono-label uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-[#EFEAE2] text-[#8C7A5E]">Annullato</span>}
                          </p>
                          <p className={`text-xs text-[#5F6B64] truncate ${f.status === "cancelled" ? "line-through" : ""}`}>
                            {f.work_type} · {fmt(f.date)}{f.farrier ? ` · ${f.farrier}` : ""}{f.edited_at ? " · modificato" : ""}
                          </p>
                        </button>
                        {perms.canOps && f.status !== "cancelled" && (
                          <button type="button" data-testid={`farrier-card-cancel-${f.id}`} onClick={(e) => { e.stopPropagation(); cancelIntervento(f); }}
                            className="shrink-0 text-xs text-[#8C532B] hover:text-[#5A3419] hover:bg-[#F0EAE1] rounded-md px-2 py-1 transition-colors">
                            Annulla
                          </button>
                        )}
                      </div>
                      {changes.length > 0 && (
                        <button type="button" data-testid={`prog-open-history-${f.id}`} onClick={() => openProg(f, true)}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-[#8C532B] hover:text-[#5A3419] transition-colors">
                          <History className="h-3 w-3" />
                          {totDays > 0 ? `Programmazione +${totDays} gg` : `${changes.length} variazioni programmazione`}
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}

      {dialog && <FarrierFormDialog initial={dialog} stableId={current.id} horses={horses} types={types} upcoming={upcoming} onClose={() => setDialog(null)} onSaved={load} />}

      {/* Postpone pick-date dialog */}
      <Dialog open={!!postponeItem} onOpenChange={(o) => !o && setPostponeItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Posticipa prossimo intervento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5F6B64]">{postponeItem?.horse_name} — attuale: {fmt(postponeItem?.next_date)}</p>
            <div><Label>Nuova data prevista</Label><DateField testId="postpone-date-input" className="mt-1.5" value={postponeDate} onChange={setPostponeDate} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostponeItem(null)}>Annulla</Button>
            <Button onClick={() => postpone(postponeItem.id, { new_date: postponeDate })} disabled={!postponeDate} data-testid="confirm-postpone-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Programmazione: cronologia posticipi (+ azioni operative solo nei Prossimi interventi) */}
      <Dialog open={!!prog} onOpenChange={(o) => { if (!o) { setProg(null); setProgDate(""); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="prog-dialog">
          <DialogHeader><DialogTitle className="font-display">Programmazione — {prog?.horse_name}</DialogTitle></DialogHeader>
          {prog && (
            <div className="space-y-4">
              <p className="text-sm text-[#5F6B64]">
                Prossimo intervento previsto: <span className="font-semibold text-[#1B3B2B]">{fmt(prog.next_date)}</span>
              </p>
              <div>
                <h4 className="font-mono-label text-xs uppercase text-[#8C532B] mb-2">Cronologia posticipi</h4>
                {(prog.next_date_changes || []).length === 0 ? (
                  <p className="text-sm text-[#5F6B64]">Nessun posticipo registrato.</p>
                ) : (
                  <div className="space-y-2">
                    {prog.next_date_changes.map((c, i) => (
                      <div key={i} data-testid={`prog-change-${i}`} className="rounded-lg border border-[#E5DEC9] bg-[#FAF8F5] px-3 py-2 text-sm">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[#1B3B2B]">
                          <span>{fmt(c.previous_date)}</span>
                          <ChevronRight className="h-3 w-3 text-[#8C532B]" />
                          <span className="font-semibold">{fmt(c.new_date)}</span>
                          {c.days ? <span className="text-xs text-[#5F6B64]">(+{c.days} gg)</span> : null}
                          {c.corrected_at ? <span className="text-xs text-[#8C532B]">· corretto</span> : null}
                        </div>
                        <div className="text-xs text-[#5F6B64] mt-0.5">
                          {(c.changed_by_name || "—")} · {fmtDT(c.changed_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!prog.readonly && perms.canOps && (
                <div className="space-y-3 pt-3 border-t border-[#F0EAE1]">
                  <div>
                    <Label>Correggi ultima data prevista</Label>
                    <div className="flex gap-2 mt-1.5">
                      <div className="flex-1"><DateField testId="prog-correct-date" value={progDate} onChange={setProgDate} /></div>
                      <Button onClick={correctPostpone} disabled={!progDate || progBusy || (prog.next_date_changes || []).length === 0}
                        data-testid="prog-correct-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                        {progBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Correggi
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={undoPostpone} disabled={progBusy || (prog.next_date_changes || []).length === 0} data-testid="prog-undo-btn">
                      Annulla ultimo posticipo
                    </Button>
                    <Button variant="outline" onClick={() => setClearConfirm(true)} disabled={progBusy} data-testid="prog-clear-btn"
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                      Elimina programmazione futura
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setProg(null); setProgDate(""); }} data-testid="prog-close-btn">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma forte: elimina SOLO la programmazione futura (stile Equiro, sostituisce window.confirm) */}
      <Dialog open={clearConfirm} onOpenChange={(o) => !o && setClearConfirm(false)}>
        <DialogContent data-testid="clear-prog-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-red-700">Elimina programmazione futura</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2D3832]">
            Verrà eliminata <span className="font-semibold">esclusivamente la programmazione futura</span> (la prossima data prevista).
            L'intervento realmente effettuato e tutto lo storico, inclusa la cronologia dei posticipi, <span className="font-semibold">rimangono conservati</span>.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearConfirm(false)}>Annulla</Button>
            <Button onClick={doClearProgramming} disabled={progBusy} data-testid="clear-prog-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white">
              {progBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Elimina programmazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Azioni sul record storico (apribile dalla riga dello Storico) */}
      <Dialog open={!!histAction} onOpenChange={(o) => !o && setHistAction(null)}>
        <DialogContent data-testid="farrier-record-actions">
          <DialogHeader><DialogTitle className="font-display">Record storico — {histAction?.horse_name}</DialogTitle></DialogHeader>
          {histAction && (
            <div className="space-y-1 text-sm">
              <p><span className="text-[#8C532B] font-medium">Tipo:</span> {histAction.work_type}</p>
              <p><span className="text-[#8C532B] font-medium">Data intervento:</span> {fmt(histAction.date)}</p>
              {histAction.farrier && <p><span className="text-[#8C532B] font-medium">Maniscalco:</span> {histAction.farrier}</p>}
              {histAction.cost != null && histAction.cost !== "" && <p><span className="text-[#8C532B] font-medium">Costo:</span> € {histAction.cost}</p>}
              {histAction.notes && <p><span className="text-[#8C532B] font-medium">Note:</span> {histAction.notes}</p>}
              {histAction.edited_at && (
                <p className="text-xs text-[#5F6B64] pt-1" data-testid="farrier-record-edited-info">
                  Modificato il {fmtDT(histAction.edited_at)}{histAction.edited_by_name ? ` da ${histAction.edited_by_name}` : ""}
                </p>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {perms.canOps && histAction?.status !== "cancelled" && (
              <Button variant="outline" data-testid="cancel-farrier-record-btn" onClick={() => { const r = histAction; setHistAction(null); cancelIntervento(r); }}
                className="text-[#8C532B] border-[#E5DEC9] hover:bg-[#F0EAE1]">
                Annulla intervento
              </Button>
            )}
            {perms.canOps && (
              <Button data-testid="edit-farrier-record-btn" onClick={() => openRecordEdit(histAction)} className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                <Pencil className="h-4 w-4 mr-2" /> Modifica record storico
              </Button>
            )}
            {perms.canManage && (
              <Button variant="outline" data-testid="delete-farrier-record-btn" onClick={() => { const r = histAction; setHistAction(null); remove(r); }}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-2" /> Elimina
              </Button>
            )}
            <Button variant="ghost" onClick={() => setHistAction(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form correzione record storico (solo dati documentali) */}
      <Dialog open={!!recEdit} onOpenChange={(o) => !o && setRecEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="farrier-record-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Modifica record storico</DialogTitle></DialogHeader>
          {recEdit && (
            <div className="space-y-4">
              <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-2 text-xs text-[#5F6B64]">
                Stai correggendo un intervento già registrato di <span className="font-semibold text-[#1B3B2B]">{recEdit.horse_name}</span>. È una correzione dello storico: la programmazione futura non verrà modificata.
              </div>
              <div>
                <Label>Tipo di lavoro *</Label>
                <Select value={recEdit.work_type} onValueChange={(v) => setRecEdit({ ...recEdit, work_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="rec-work-type"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data intervento *</Label><DateField testId="rec-date" className="mt-1.5" value={recEdit.date} onChange={(v) => setRecEdit({ ...recEdit, date: v })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Maniscalco</Label><Input className="mt-1.5" data-testid="rec-farrier" value={recEdit.farrier} onChange={(e) => setRecEdit({ ...recEdit, farrier: e.target.value })} /></div>
                <div><Label>Costo (€)</Label><Input type="number" step="0.01" className="mt-1.5" data-testid="rec-cost" value={recEdit.cost} onChange={(e) => setRecEdit({ ...recEdit, cost: e.target.value })} /></div>
              </div>
              <div><Label>Note</Label><Textarea className="mt-1.5" data-testid="rec-notes" value={recEdit.notes} onChange={(e) => setRecEdit({ ...recEdit, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecEdit(null)}>Annulla</Button>
            <Button onClick={() => setRecEditConfirm(true)} disabled={!recEdit?.date || !recEdit?.work_type} data-testid="rec-save-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              Salva correzione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma forte modifica record storico */}
      <Dialog open={recEditConfirm} onOpenChange={(o) => !o && setRecEditConfirm(false)}>
        <DialogContent data-testid="rec-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-[#8C532B]">Confermi la modifica del record storico?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2D3832]">
            Stai modificando un <span className="font-semibold">dato storico</span> già registrato. Questa è una <span className="font-semibold">correzione</span> dello storico, non una nuova esecuzione dell'intervento: la programmazione futura e le eventuali prossime scadenze non verranno modificate.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecEditConfirm(false)}>Annulla</Button>
            <Button onClick={saveRecordEdit} disabled={recSaving} data-testid="rec-confirm-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {recSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Conferma modifica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Self-contained create/edit dialog for a Mascalcia record. Single source used by the Mascalcia
// page AND by Horse Detail (quick-add). Owns its working state (seeded from `initial`) and all
// business logic (supersede confirm, single/bulk/overrides, next-date chaining). No duplication.
export function FarrierFormDialog({ stableId, horses, types: propTypes, upcoming: propUpcoming, initial, onClose, onSaved }) {
  const [dialog, setDialog] = useState(() => ({ ...EMPTY, ...initial }));
  const [saving, setSaving] = useState(false);
  const [fetchedTypes, setFetchedTypes] = useState(null);
  const [fetchedUpcoming, setFetchedUpcoming] = useState(null);
  useEffect(() => {
    if (!propTypes) api.get(`/stables/${stableId}/farrier-types`).then((r) => setFetchedTypes(r.data)).catch(() => setFetchedTypes([]));
    if (!propUpcoming) api.get(`/stables/${stableId}/farrier/upcoming`).then((r) => setFetchedUpcoming(r.data)).catch(() => setFetchedUpcoming([]));
  }, [propTypes, propUpcoming, stableId]);
  const types = propTypes || fetchedTypes || [];
  const upcoming = propUpcoming || fetchedUpcoming || [];
  const doSaveFarrier = async (ids, isEdit) => {
    const cost = dialog.cost === "" ? null : Number(dialog.cost);
    const ovAll = dialog.overrides || {};
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { horse_id: dialog.horse_id, date: dialog.date, work_type: dialog.work_type, cost, farrier: dialog.farrier || null, notes: dialog.notes || null, next_date: dialog.next_date || null };
        await api.put(`/stables/${stableId}/farrier/${dialog.id}`, payload);
        toast.success("Intervento salvato"); onSaved && onSaved(); onClose();
      } else if (ids.length === 1) {
        const hid = ids[0]; const ov = ovAll[hid] || {};
        const payload = { horse_id: hid, date: dialog.date, work_type: ov.type || dialog.work_type,
          cost: (ov.cost != null && ov.cost !== "") ? Number(ov.cost) : cost,
          farrier: dialog.farrier || null, notes: dialog.notes || null, next_date: ov.next_date || dialog.next_date || null };
        const res = await api.post(`/stables/${stableId}/farrier`, payload);
        toast.success("Intervento salvato"); const createdId = res?.data?.id; onSaved && onSaved();
        if (!payload.next_date) {
          const yes = await confirmAction({ title: "Programmare il prossimo intervento?", description: "Puoi impostare ora la data del prossimo intervento per questo cavallo, oppure farlo in un secondo momento. Nessuna data viene creata automaticamente.", confirmLabel: "Programma ora", cancelLabel: "Più tardi", danger: false });
          if (yes && createdId) { setDialog({ ...EMPTY, id: createdId, horse_id: hid, work_type: payload.work_type || "", farrier: payload.farrier || "", date: payload.date, cost: payload.cost ?? "", notes: payload.notes || "", next_date: "" }); setSaving(false); return; }
        }
        onClose();
      } else {
        const items = ids.map((hid) => { const ov = ovAll[hid] || {}; return { horse_id: hid, work_type: ov.type || dialog.work_type,
          cost: (ov.cost != null && ov.cost !== "") ? Number(ov.cost) : cost, next_date: ov.next_date || dialog.next_date || null }; });
        const res = await api.post(`/stables/${stableId}/farrier/bulk`, { date: dialog.date, farrier: dialog.farrier || null, notes: dialog.notes || null, items });
        toast.success(`${res.data?.count || items.length} interventi registrati`); onSaved && onSaved(); onClose();
      }
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    const isEdit = !!dialog.id;
    const ids = isEdit ? (dialog.horse_id ? [dialog.horse_id] : []) : (dialog.horse_ids || []);
    if (!ids.length) { toast.error("Seleziona almeno un cavallo"); return; }
    if (!dialog.work_type) { toast.error("Seleziona il tipo di lavoro"); return; }
    if (!isEdit) {
      const affected = ids.map((id) => (upcoming || []).find((u) => u.horse_id === id && u.next_date)).filter(Boolean);
      if (affected.length) {
        const names = affected.map((u) => `${u.horse_name} (${farrierStatus(u.next_date) === "scaduto" ? "scaduto" : "in programma " + fmt(u.next_date)})`).join(", ");
        const ok = await confirmAction({ title: `Chiudere ${affected.length} intervento${affected.length > 1 ? "i" : ""} Mascalcia esistente${affected.length > 1 ? "i" : ""}?`, description: `Il riferimento Mascalcia precedente di: ${names} verrà chiuso e spostato nello storico (non eliminato). Il nuovo intervento diventa il riferimento operativo.`, confirmLabel: `Conferma per ${ids.length} cavall${ids.length > 1 ? "i" : "o"}`, cancelLabel: "Annulla", danger: false });
        if (!ok) return;
      }
    }
    await doSaveFarrier(ids, isEdit);
  };
  if (!dialog) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{dialog?.id ? "Modifica intervento" : "Registra intervento"}</DialogTitle></DialogHeader>
        {(() => {
          const selIds = dialog.id ? (dialog.horse_id ? [dialog.horse_id] : []) : (dialog.horse_ids || []);
          const multiSel = !dialog.id && selIds.length > 1;
          const ov = dialog.overrides || {};
          const anyIndivCost = selIds.some((id) => ov[id]?.cost != null && ov[id]?.cost !== "");
          const anyIndivNext = selIds.some((id) => ov[id]?.next_date);
          return (
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>{dialog.id ? "Cavallo *" : "Cavalli *"}</Label>
              <div className="mt-1.5">
                {dialog.id
                  ? <HorsePicker testId="farrier-horse" horses={horses} value={dialog.horse_id} onChange={(id) => setDialog({ ...dialog, horse_id: id })} />
                  : <HorsePicker testId="farrier-horse" horses={horses} multiple value={dialog.horse_ids || []} onChange={(ids) => setDialog({ ...dialog, horse_ids: ids })} />}
              </div>
              {!dialog.id && <p className="text-[11px] text-[#8C7A5E] mt-1">Seleziona uno o più cavalli. Con più cavalli l'intervento viene registrato singolarmente per ciascuno.</p>}
            </div>
            <div>
              <Label>{multiSel ? "Tipo di lavoro predefinito *" : "Tipo di lavoro *"}</Label>
              <Select value={dialog.work_type} onValueChange={(v) => setDialog({ ...dialog, work_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="farrier-type"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {multiSel ? (
              <>
                <div><Label>Data intervento *</Label>
                  <DateField testId="farrier-date" className="mt-1.5" value={dialog.date} onChange={(v) => setDialog({ ...dialog, date: v })} />
                  <FutureDateWarning value={dialog.date} testId="farrier-date-future" /></div>
                <div>
                  <Label className="text-[#5F6B64] text-xs">Prossimo intervento comune (opzionale) — calcola durata dalla data</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {[1, 2, 3, 4, 6].map((m) => (
                      <button key={m} type="button" data-testid={`farrier-common-interval-${m}m`} disabled={!dialog.date || anyIndivNext}
                        onClick={() => setDialog({ ...dialog, next_date: addMonthsISO(dialog.date, m) })}
                        className="text-xs rounded-full border border-[#E5DEC9] bg-white px-3 py-1 text-[#8C532B] hover:border-[#8C532B] disabled:opacity-40 transition-colors">{m} mes{m === 1 ? "e" : "i"}</button>
                    ))}
                    <button type="button" data-testid="farrier-common-interval-none" disabled={anyIndivNext} onClick={() => setDialog({ ...dialog, next_date: "" })}
                      className="text-xs rounded-full border border-[#E5DEC9] bg-white px-3 py-1 text-[#8C532B] hover:border-[#8C532B] disabled:opacity-40 transition-colors">Nessuno</button>
                  </div>
                  {dialog.next_date && <p className="text-xs text-[#2E7D32] mt-1.5">Prossimo intervento comune: {fmt(dialog.next_date)}</p>}
                  {anyIndivNext && <p className="text-[11px] text-[#8C7A5E] mt-1">Disattivato: sono impostati prossimi interventi individuali.</p>}
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Data *</Label><DateField testId="farrier-date" className="mt-1.5" value={dialog.date} onChange={(v) => setDialog({ ...dialog, date: v })} /><FutureDateWarning value={dialog.date} testId="farrier-date-future" /></div>
                  <div><Label>Prossimo intervento</Label><DateField testId="farrier-next" className="mt-1.5" value={dialog.next_date || ""} onChange={(v) => setDialog({ ...dialog, next_date: v })} /></div>
                </div>
                <div>
                  <Label className="text-[#5F6B64] text-xs">Oppure calcola dalla data dell'intervento</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {[1, 2, 3, 4, 6].map((m) => (
                      <button key={m} type="button" data-testid={`farrier-interval-${m}m`} disabled={!dialog.date}
                        onClick={() => setDialog({ ...dialog, next_date: addMonthsISO(dialog.date, m) })}
                        className="text-xs rounded-full border border-[#E5DEC9] bg-white px-3 py-1 text-[#8C532B] hover:border-[#8C532B] disabled:opacity-40 transition-colors">
                        {m} mes{m === 1 ? "e" : "i"}
                      </button>
                    ))}
                    <button type="button" data-testid="farrier-interval-custom" onClick={() => setDialog({ ...dialog, next_date: "" })}
                      className="text-xs rounded-full border border-[#E5DEC9] bg-white px-3 py-1 text-[#8C532B] hover:border-[#8C532B] transition-colors">
                      Personalizzato
                    </button>
                  </div>
                  {dialog.next_date && (
                    <p className="text-xs text-[#2E7D32] mt-1.5" data-testid="farrier-next-preview">
                      Prossimo intervento previsto: {fmt(dialog.next_date)}
                    </p>
                  )}
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Maniscalco</Label><Input className="mt-1.5" value={dialog.farrier || ""} onChange={(e) => setDialog({ ...dialog, farrier: e.target.value })} /></div>
              <div><Label>Costo per intervento (€)</Label><Input type="number" step="0.01" className="mt-1.5" data-testid="farrier-cost" disabled={anyIndivCost} value={dialog.cost} onChange={(e) => setDialog({ ...dialog, cost: e.target.value })} /></div>
            </div>
            <div><Label>Note{multiSel ? " (comuni)" : ""}</Label><Textarea className="mt-1.5" value={dialog.notes || ""} onChange={(e) => setDialog({ ...dialog, notes: e.target.value })} /></div>
            {multiSel && (
              <div>
                <Label className="text-xs text-[#5F6B64]">Riepilogo per cavallo · override tipo / costo / prossimo intervento</Label>
                <div className="mt-1.5 rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden">
                  {selIds.map((hid) => { const h = horses.find((x) => x.id === hid); const rv = ov[hid] || {}; return (
                    <div key={hid} data-testid={`bulk-row-${hid}`} className="px-3 py-2 space-y-1.5">
                      <p className="text-sm font-medium text-[#1B3B2B] truncate">{h?.display_name || "—"}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                        <Select value={rv.type || dialog.work_type} onValueChange={(v) => setDialog({ ...dialog, overrides: { ...ov, [hid]: { ...rv, type: v } } })}>
                          <SelectTrigger className="h-8" data-testid={`bulk-row-type-${hid}`}><SelectValue placeholder="Tipo" /></SelectTrigger>
                          <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" step="0.01" className="h-8" data-testid={`bulk-row-cost-${hid}`} disabled={dialog.cost !== ""}
                          placeholder={dialog.cost !== "" ? `${dialog.cost} € (comune)` : "Costo € (opz.)"} value={rv.cost ?? ""}
                          onChange={(e) => setDialog({ ...dialog, overrides: { ...ov, [hid]: { ...rv, cost: e.target.value } } })} />
                        <DateField testId={`bulk-row-next-${hid}`} value={rv.next_date || ""} disabled={!!dialog.next_date}
                          onChange={(v) => setDialog({ ...dialog, overrides: { ...ov, [hid]: { ...rv, next_date: v } } })} />
                      </div>
                    </div>
                  ); })}
                </div>
                <p className="text-[11px] text-[#8C7A5E] mt-1">Lascia vuoto per usare i valori comuni. Il costo è per singolo intervento.</p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onClose()}>Annulla</Button>
              <Button type="submit" disabled={saving} data-testid="save-farrier-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {multiSel ? `Registra ${selIds.length} interventi` : "Salva"}
              </Button>
            </DialogFooter>
          </form>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
